import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, Articulo } from '@/types';

interface CartState {
  items: CartItem[];
  clienteId: string | null;
  addItem: (articulo: Articulo, cantidad: number, descuento?: number) => void;
  addMultipleItems: (items: { articulo: Articulo; cantidad: number; descuento?: number }[]) => void;
  removeItem: (articuloId: number) => void;
  updateQuantity: (articuloId: number, cantidad: number) => void;
  updateDescuento: (articuloId: number, descuento: number) => void;
  clearCart: () => void;
  setClienteId: (clienteId: string) => void;
  getTotal: () => number;
  getSubtotal: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      clienteId: null,
      
      addItem: (articulo, cantidad, descuento = 0) => {
        const items = get().items;
        const existingItem = items.find(item => item.articulo.id === articulo.id);
        
        if (existingItem) {
          set({
            items: items.map(item =>
              item.articulo.id === articulo.id
                ? { ...item, cantidad: item.cantidad + cantidad }
                : item
            ),
          });
        } else {
          set({ items: [...items, { articulo, cantidad, descuento }] });
        }
      },
      
      addMultipleItems: (newItems) => {
        const currentItems = get().items;
        const itemsMap = new Map(currentItems.map(item => [item.articulo.id, item]));
        
        newItems.forEach(({ articulo, cantidad, descuento = 0 }) => {
          const existing = itemsMap.get(articulo.id);
          if (existing) {
            itemsMap.set(articulo.id, {
              ...existing,
              cantidad: existing.cantidad + cantidad,
            });
          } else {
            itemsMap.set(articulo.id, { articulo, cantidad, descuento });
          }
        });
        
        set({ items: Array.from(itemsMap.values()) });
      },
      
      removeItem: (articuloId) => {
        set({ items: get().items.filter(item => item.articulo.id !== articuloId) });
      },
      
      updateQuantity: (articuloId, cantidad) => {
        if (cantidad <= 0) {
          get().removeItem(articuloId);
          return;
        }
        set({
          items: get().items.map(item =>
            item.articulo.id === articuloId ? { ...item, cantidad } : item
          ),
        });
      },
      
      updateDescuento: (articuloId, descuento) => {
        set({
          items: get().items.map(item =>
            item.articulo.id === articuloId ? { ...item, descuento } : item
          ),
        });
      },
      
      clearCart: () => {
        set({ items: [], clienteId: null });
      },
      
      setClienteId: (clienteId) => {
        set({ clienteId });
      },
      
      getSubtotal: () => {
        return get().items.reduce((total, item) => {
          const precioConDescuento = item.articulo.precio * (1 - item.descuento / 100);
          return total + precioConDescuento * item.cantidad;
        }, 0);
      },
      
      getTotal: () => {
        const subtotal = get().getSubtotal();
        const iva = 0.21; // 21% IVA por defecto
        return subtotal * (1 + iva);
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);
